/**
 * Settings API
 * Handles organization settings, campus management, and user management
 *
 * Endpoints:
 * - GET /api/settings/organization/ - Get organization settings
 * - PATCH /api/settings/organization/ - Update organization settings
 * - POST /api/settings/organization/logo/ - Upload organization logo
 * - POST /api/settings/organization/branding/ - Save branding fields and staged assets
 * - GET /api/settings/authentication/ - Get tenant auth settings
 * - PATCH /api/settings/authentication/ - Update tenant auth settings
 */

import apiClient from './client'

function tenantRequest({ tenantId, signal } = {}) {
  return { signal, ...(tenantId ? { headers: { 'X-Tenant-ID': tenantId } } : {}) }
}

/**
 * Get organization settings for current tenant
 *
 * @returns {Promise<Object>} Organization settings
 * @example
 * {
 *   id: "1",
 *   name: "First Baptist Church",
 *   email: "info@fbc.org",
 *   phone: "(555) 123-4567",
 *   website: "https://fbc.org",
 *   address: "123 Main St\nAnytown, ST 12345",
 *   logo_url: "https://cdn.example.com/logo.png",
 *   updated_at: "2025-10-12T18:00:00Z"
 * }
 */
export async function getOrganizationSettings(options) {
  const response = await apiClient.get('/settings/organization/', tenantRequest(options))

  // Parse JSON:API response
  const { data } = response.data
  return {
    id: data.id,
    ...data.attributes,
  }
}

/**
 * Update organization settings
 *
 * @param {Object} updates - Settings to update
 * @param {string} [updates.name] - Organization name
 * @param {string} [updates.email] - Contact email
 * @param {string} [updates.phone] - Contact phone
 * @param {string} [updates.website] - Organization website
 * @param {string} [updates.address] - Physical address
 * @returns {Promise<Object>} Updated organization settings
 */
export async function updateOrganizationSettings(id, updates, options) {
  const response = await apiClient.patch(`/settings/organization/${id}/`, updates, tenantRequest(options))

  // Parse JSON:API response
  const { data } = response.data
  return {
    id: data.id,
    ...data.attributes,
  }
}

/**
 * Upload organization logo
 *
 * @param {File} file - Logo image file (PNG, JPEG, WebP)
 * @returns {Promise<Object>} Updated organization settings with new logo_url
 */
export async function uploadLogo(file, options) {
  const formData = new FormData()
  formData.append('logo', file)

  const response = await apiClient.post('/settings/organization/logo/', formData, {
    ...tenantRequest(options),
    headers: {
      ...tenantRequest(options).headers,
      'Content-Type': 'multipart/form-data',
    },
  })

  // Parse JSON:API response
  const { data } = response.data
  return {
    id: data.id,
    ...data.attributes,
  }
}

/** Save the draft and staged asset changes together; previews never upload files. */
export async function saveBranding({ settings, files = {}, remove = {} }, options) {
  const formData = new FormData()
  formData.append('settings', JSON.stringify(settings))
  for (const variant of ['logo', 'dark_logo', 'icon']) {
    if (files[variant]) formData.append(variant, files[variant])
    if (remove[variant]) formData.append(`remove_${variant}`, 'true')
  }
  const request = tenantRequest(options)
  const response = await apiClient.post('/settings/organization/branding/', formData, {
    ...request,
    headers: { ...request.headers, 'Content-Type': 'multipart/form-data' },
  })
  const { data } = response.data
  return { id: data.id, ...data.attributes }
}

/**
 * Get tenant authentication settings.
 * @returns {Promise<Object>}
 */
export async function getAuthenticationSettings() {
  const response = await apiClient.get('/settings/authentication/')
  return response.data?.data?.attributes || {}
}

/**
 * Update tenant authentication settings.
 * @param {Object} payload
 * @returns {Promise<Object>}
 */
export async function updateAuthenticationSettings(payload) {
  const response = await apiClient.patch('/settings/authentication/', payload)
  return response.data?.data?.attributes || {}
}

/**
 * Verify provider configuration.
 *
 * A failed verify answers 400 with `{ data: { attributes: { message } } }` -
 * a plain resource envelope, not the `{ errors: [...] }` shape the client's
 * response interceptor understands. Left alone, `error.apiError.message`
 * falls back to axios's generic "Request failed with status code 400"
 * instead of the provider's actual reason, so this pulls the real message
 * out of the envelope before rethrowing.
 *
 * @param {string} provider
 * @returns {Promise<Object>}
 */
export async function verifyAuthenticationProvider(provider) {
  try {
    const response = await apiClient.post(`/settings/authentication/providers/${provider}/verify/`)
    return response.data?.data?.attributes || {}
  } catch (error) {
    const detail = error.response?.data?.data?.attributes?.message
    if (detail) {
      error.apiError = { ...(error.apiError || {}), message: detail }
    }
    throw error
  }
}

/**
 * Relock password failsafe after OAuth recovers.
 * @returns {Promise<Object>}
 */
export async function relockAuthenticationPassword() {
  const response = await apiClient.post('/settings/authentication/password/relock/')
  return response.data?.data?.attributes || {}
}

/**
 * Start connecting Microsoft sign-in through Graphit's Entra app: returns the
 * Microsoft sign-in URL the administrator is sent to.
 * @returns {Promise<{authorization_url?: string}>}
 */
export async function startMicrosoftConnect() {
  const response = await apiClient.post('/settings/authentication/providers/microsoft/connect/')
  return response.data?.data?.attributes || {}
}

/**
 * Finish connecting: hand the backend what Microsoft redirected back with.
 * The backend validates the ID token and binds its directory.
 * @param {{code?: string, state?: string, error?: string, error_description?: string}} params
 * @returns {Promise<Object>} The updated authentication settings attributes
 */
export async function completeMicrosoftConnect(params) {
  const response = await apiClient.post('/settings/authentication/providers/microsoft/connect/complete/', params)
  return response.data?.data?.attributes || {}
}

/**
 * Disconnect Microsoft sign-in from its directory (platform mode).
 * @returns {Promise<Object>} The updated authentication settings attributes
 */
export async function disconnectMicrosoft() {
  const response = await apiClient.post('/settings/authentication/providers/microsoft/disconnect/')
  return response.data?.data?.attributes || {}
}
